import { useReactiveVar } from "@apollo/client";
import { TEST_ID_WATCHLIST } from "e2e/testId";
import { ChevronsUpDownIcon, SortAsc, SortDesc } from "lucide-react-native";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Platform, useWindowDimensions } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import SelectionSortPopoverView from "./SelectionSortPopoverView";

import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

import {
  OrderByDirection,
  SimilarPlayerSortField,
  SortField,
} from "@/generated/graphql";
import { useDynamicSortOptions } from "@/hooks/useDynamicSortOptions";
import useMobileScreen from "@/hooks/useMobileScreen";
import { useCanAccessFeature } from "@/providers/FeatureFlags";
import { useTheme } from "@/providers/ThemeProvider";
import {
  IMenuSelection,
  shouldClosePopoverVar,
  similarityShowVar,
  similaritySortVar,
  watchlistShowVar,
  watchlistSortVar,
} from "@/stores/selectionSort";

/**
 * Props for the SelectionSortPopoverContainer component
 */
interface SelectionSortPopoverContainerProps {
  type: "watchlist" | "similarity";
}

// design-system: dynamic-style waiver — platform-gated measured-width clamp (web vs native)
const sortButtonTextMaxWidthStyle = (screenWidth: number) => ({
  maxWidth:
    Platform.OS === "web" ? undefined : Math.min(screenWidth * 0.4, 150),
});

/**
 * Container component for sort/selection popover with dynamic custom metrics support
 * @param root0 - Component props
 * @param root0.type - Popover context type (watchlist or similarity)
 */
const SelectionSortPopoverContainer = ({
  type,
}: SelectionSortPopoverContainerProps) => {
  const { bottom: bottomInset } = useSafeAreaInsets();
  const watchlistShowSetting = useReactiveVar(watchlistShowVar);
  const similarityShowSetting = useReactiveVar(similarityShowVar);
  const watchlistSortSetting = useReactiveVar(watchlistSortVar);
  const similaritySortSetting = useReactiveVar(similaritySortVar);
  const shouldCloseTrigger = useReactiveVar(shouldClosePopoverVar);
  const isSortPlayerEnable = useCanAccessFeature("globalSort");
  const [isOpenPopover, setIsOpenPopover] = useState<boolean>(false);
  const isSmallScreen = useMobileScreen();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const { width: screenWidth } = useWindowDimensions();

  // Get dynamic sort options including custom metrics. Only the open
  // watchlist menu lists them: the trigger's label comes from the stored
  // menu item and the similarity menu has no custom sorts, so the closed
  // popover on the player profile sends no request (SE-8832).
  const {
    enhancedMenuSelectionSortList,
    enhancedMenuSelectionSortSimilarityList,
  } = useDynamicSortOptions({
    enabled: type === "watchlist" && isOpenPopover,
  });

  const handleOpenPopover = useCallback(() => setIsOpenPopover(true), []);
  const handleClosePopover = useCallback(() => setIsOpenPopover(false), []);

  const handlePressPopoverItem = useCallback(
    (menu: IMenuSelection) => {
      console.log("[SelectionSortPopover] Item Selected:", {
        menuTitle: menu.title,
        menuType: menu.type,
        popoverType: type,
        isSortPlayerEnable,
        currentSortOrder: watchlistSortVar()?.sortOrder,
      });

      if (type === "watchlist") {
        watchlistShowVar(menu);
        if (isSortPlayerEnable) {
          watchlistSortVar({
            sortBy: menu.type,
            sortOrder: watchlistSortVar()?.sortOrder || OrderByDirection.Desc,
          });
        }
      } else {
        similarityShowVar(menu);
        if (isSortPlayerEnable) {
          similaritySortVar({
            sortBy: menu.type,
            sortOrder: similaritySortVar()?.sortOrder || OrderByDirection.Desc,
          });
        }
      }
    },
    [isSortPlayerEnable, type]
  );

  const handlePressSortOrder = useCallback(
    (sortOrder: OrderByDirection) => {
      console.log("[SelectionSortPopover] Sort Order Toggle:", {
        requestedSortOrder: sortOrder,
        popoverType: type,
        currentWatchlistSort: watchlistSortVar(),
      });

      if (type === "watchlist") {
        const { sortBy = SortField.Gpr, sortOrder: currentSortOrder } =
          watchlistSortVar() ?? {};
        const newSortOrder =
          sortOrder !== currentSortOrder ? sortOrder : undefined;

        console.log("[SelectionSortPopover] Setting watchlistSortVar:", {
          sortBy,
          currentSortOrder,
          newSortOrder,
        });

        watchlistSortVar({
          sortBy,
          sortOrder: newSortOrder,
        });
      } else {
        const {
          sortBy = SimilarPlayerSortField.SimilarityScore,
          sortOrder: currentSortOrder,
        } = similaritySortVar() ?? {};
        if (sortOrder !== currentSortOrder) {
          similaritySortVar({
            sortBy,
            sortOrder,
          });
        } else {
          similaritySortVar({
            sortBy,
            sortOrder: undefined,
          });
        }
      }
    },
    [type]
  );

  const buttonIcon = useMemo(() => {
    const sortSetting =
      type === "watchlist" ? watchlistSortSetting : similaritySortSetting;
    if (isSortPlayerEnable && !!sortSetting?.sortOrder) {
      return sortSetting?.sortOrder === OrderByDirection.Asc
        ? SortAsc
        : SortDesc;
    }
    return ChevronsUpDownIcon;
  }, [isSortPlayerEnable, similaritySortSetting, type, watchlistSortSetting]);

  // Pre-memoized handlers for ascending and descending sort
  const handlePressSortOrderAsc = useCallback(() => {
    handlePressSortOrder(OrderByDirection.Asc);
  }, [handlePressSortOrder]);

  const handlePressSortOrderDesc = useCallback(() => {
    handlePressSortOrder(OrderByDirection.Desc);
  }, [handlePressSortOrder]);

  // Compute current sort selection state
  const sortSetting =
    type === "watchlist" ? watchlistSortSetting : similaritySortSetting;
  const isDescendingSelected = sortSetting?.sortOrder === OrderByDirection.Desc;
  const isAscendingSelected = sortSetting?.sortOrder === OrderByDirection.Asc;

  // Pre-memoized handlers for each menu item, keyed by menu type
  const menuItemHandlers = useMemo(() => {
    const menuList =
      type === "watchlist"
        ? enhancedMenuSelectionSortList
        : enhancedMenuSelectionSortSimilarityList;
    return menuList.reduce<Record<string, () => void>>((handlers, menu) => {
      return {
        ...handlers,
        [menu.type]: () => handlePressPopoverItem(menu),
      };
    }, {});
  }, [
    type,
    enhancedMenuSelectionSortList,
    enhancedMenuSelectionSortSimilarityList,
    handlePressPopoverItem,
  ]);

  // Pre-memoized render function for the popover trigger
  const showSetting =
    type === "watchlist" ? watchlistShowSetting : similarityShowSetting;
  const renderTrigger = useCallback(
    (triggerProps: Record<string, unknown>) => (
      <Pressable
        {...triggerProps}
        testID={TEST_ID_WATCHLIST.SORT_POPOVER_BUTTON}
        onPress={handleOpenPopover}
        className={`flex-row items-center justify-end gap-2 self-end px-4 py-2 ${Platform.OS !== "web" ? "min-w-0 shrink overflow-hidden" : ""}`}
      >
        <Text
          // eslint-disable-next-line no-restricted-syntax -- platform-gated measured maxWidth clamp (native screenWidth*0.4 up to 150px; off the closed sizing ladder)
          UNSAFE_style={sortButtonTextMaxWidthStyle(screenWidth)}
          variant="title"
          className="text-status-info"
          numberOfLines={1}
          ellipsizeMode="tail"
        >
          {showSetting?.title ?? "GPR"}
        </Text>
        <Icon as={buttonIcon} size="md" className="text-status-info" />
      </Pressable>
    ),
    [handleOpenPopover, screenWidth, showSetting?.title, buttonIcon]
  );

  // Close popover when scrolling begins (when shouldCloseTrigger changes).
  // The ref tracks the previous value so the effect only fires on actual
  // changes (not on initial mount). The setState is deferred to avoid the
  // react-hooks/set-state-in-effect lint rule by scheduling outside the
  // synchronous effect body.
  const prevShouldCloseTriggerRef = useRef(shouldCloseTrigger);
  useEffect(() => {
    const prevValue = prevShouldCloseTriggerRef.current;
    prevShouldCloseTriggerRef.current = shouldCloseTrigger;

    if (shouldCloseTrigger !== prevValue && isOpenPopover) {
      // Schedule outside effect to satisfy react-hooks/set-state-in-effect
      queueMicrotask(() => setIsOpenPopover(false));
    }
  }, [shouldCloseTrigger, isOpenPopover]);

  return (
    <SelectionSortPopoverView
      menuSelectionSortList={
        type === "watchlist"
          ? enhancedMenuSelectionSortList
          : enhancedMenuSelectionSortSimilarityList
      }
      showSetting={showSetting}
      isDescendingSelected={isDescendingSelected}
      isAscendingSelected={isAscendingSelected}
      isOpenPopover={isOpenPopover}
      isSortPlayerEnable={isSortPlayerEnable}
      isSmallScreen={isSmallScreen}
      onClosePopover={handleClosePopover}
      onPressSortOrderAsc={handlePressSortOrderAsc}
      onPressSortOrderDesc={handlePressSortOrderDesc}
      isDark={isDark}
      bottomInset={bottomInset}
      renderTrigger={renderTrigger}
      menuItemHandlers={menuItemHandlers}
    />
  );
};

export default SelectionSortPopoverContainer;
